import { TVL_BREAKDOWN_COLORS } from '@/constants/charts'
import type { Vault } from '@/types/vault'
import { shortenAddress } from '@/utils/address/shortenAddress'
import { cn } from '@/utils/cn'
import { getExplorerLink } from '@/utils/explorer'
import { toDecimal } from '@vaultsfyi/common/utils'
import { formatAmount, formatDecimalToPercentage, formatTokenValue } from '@vaultsfyi/common/utils'
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { type Address, formatUnits } from 'viem'
import { ChartContainer } from './chartContainer'
import { ChartTooltip } from './chartTooltip'

const CHART_RADIUS = 104
const CHART_SIZE = CHART_RADIUS * 2
const REST_HOLDERS_PATTERN_KEY = 'rest-holders-pattern'
const LABEL_X_OFFSET = -4
const LABEL_Y_PRIMARY = 24
const LABEL_Y_SECONDARY = 34

interface Props {
  vault: Vault
}

export const HoldersChart = ({ vault }: Props) => {
  const { chartData, restHoldersCount } = getHoldersChartData(vault)

  return (
    <div className="relative flex h-fit w-fit flex-col items-center justify-center gap-4">
      <ChartContainer className="h-52 min-h-52 w-52 min-w-52">
        <ResponsiveContainer>
          <PieChart>
            <defs>
              <RestHoldersPattern />
            </defs>
            <Pie
              data={chartData}
              dataKey="proportion"
              cx="50%"
              cy="50%"
              outerRadius={CHART_RADIUS - 1}
              startAngle={86}
              endAngle={-274}
              isAnimationActive={false}
              fillOpacity={1}
              paddingAngle={3.25}
              labelLine={false}
              className="group"
            >
              {chartData.map(({ color, role, link }, index) => (
                <Cell
                  key={index}
                  fill={role === 'holder' ? color : `url(#${REST_HOLDERS_PATTERN_KEY})`}
                  fillOpacity={1}
                  onClick={() => link && window.open(link, '_blank')}
                  className={cn(
                    'transition-all',
                    'hover:!opacity-100 group-hover:opacity-60',
                    link && 'clickable-cell cursor-pointer',
                    role
                  )}
                  stroke={role === 'rest' ? 'rgb(var(--border))' : color}
                  strokeWidth={2}
                />
              ))}
              <Label content={(props) => <CenterLabel {...props} restHoldersCount={restHoldersCount} />} />
            </Pie>
            <Tooltip
              wrapperStyle={{ zIndex: 50 }}
              content={({ payload }) => <HoldersChartTooltip data={payload?.[0]?.payload} lpToken={vault.lpToken} />}
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>
      <TotalHoldersLabel totalCount={vault.holdersData?.totalCount} />
    </div>
  )
}

const RestHoldersPattern = () => {
  return (
    <pattern
      id={REST_HOLDERS_PATTERN_KEY}
      x="0"
      y="0"
      width={CHART_SIZE}
      height={CHART_SIZE}
      patternUnits="userSpaceOnUse"
      patternContentUnits="userSpaceOnUse"
    >
      <circle cx={CHART_RADIUS} cy={CHART_RADIUS} r={CHART_RADIUS} fill="rgb(var(--bg-surface))" />
    </pattern>
  )
}

interface CenterLabelProps {
  viewBox?: unknown
  restHoldersCount: number
}

const CenterLabel = ({ viewBox, restHoldersCount }: CenterLabelProps) => {
  const hasValidViewBox = (vb: unknown): vb is { cx: number; cy: number } => {
    return typeof vb === 'object' && vb !== null && 'cx' in vb && 'cy' in vb
  }

  if (!hasValidViewBox(viewBox) || restHoldersCount === 0) {
    return null
  }

  const x = (viewBox.cx ?? 0) + LABEL_X_OFFSET

  return (
    <text x={x} y={LABEL_Y_PRIMARY} textAnchor="end" dominantBaseline="middle" className="pointer-events-none">
      <tspan x={x} y={LABEL_Y_PRIMARY} className="fill-heading-foreground font-bold text-xs">
        {formatAmount(restHoldersCount)}
      </tspan>
      <tspan x={x} y={LABEL_Y_SECONDARY} className="fill-body-foreground [font-size:10px]">
        others
      </tspan>
    </text>
  )
}

interface TotalHoldersLabelProps {
  totalCount?: number
}

const TotalHoldersLabel = ({ totalCount }: TotalHoldersLabelProps) => {
  return (
    <div
      className={cn(
        'absolute flex aspect-square size-3/5 flex-col items-center justify-center gap-1.5 rounded-full bg-page text-center',
        'before: before:absolute before:size-[calc(100%-14px)] before:rounded-full before:border-2'
      )}
    >
      <p className="font-bold text-heading-foreground text-xl">{totalCount ? formatAmount(totalCount) : '--'}</p>
      <p className="text-xs"># of holders</p>
    </div>
  )
}

interface HoldersChartTooltipProps {
  data: HoldersChartDataCell | undefined
  lpToken: Vault['lpToken']
}

const HoldersChartTooltip = ({ data, lpToken }: HoldersChartTooltipProps) => {
  if (!data || !lpToken) {
    return null
  }

  return (
    <ChartTooltip>
      <span>{data.label}</span>
      <p className="font-semibold">
        {formatTokenValue({ value: data.rawValue, decimals: lpToken.decimals })} {lpToken.symbol} (
        {formatDecimalToPercentage(toDecimal(data.proportion))})
      </p>
    </ChartTooltip>
  )
}

interface HoldersChartDataCell {
  label: string
  value: number
  rawValue: bigint
  proportion: number
  color: string
  role: 'holder' | 'rest'
  link?: string
}

interface HoldersChartResult {
  chartData: HoldersChartDataCell[]
  restHoldersCount: number
}

const getHoldersChartData = (vault: Vault): HoldersChartResult => {
  const { holdersData, network, lpToken } = vault

  const totalBalance = BigInt(holdersData?.totalBalance ?? 0)
  const balanceDenominator = totalBalance === 0n ? 1n : totalBalance
  const topHolders = holdersData?.topHolders ?? []

  const topHoldersBalance = topHolders.reduce((sum, holder) => sum + BigInt(holder.lpTokenBalance), 0n)

  const topHoldersData: HoldersChartDataCell[] = topHolders
    .map(({ address, lpTokenBalance }, index) => {
      const balance = BigInt(lpTokenBalance)
      const holderProportion = (Number(balance) / Number(balanceDenominator)) * 100
      return {
        label: shortenAddress(address as Address),
        value: Number(formatUnits(balance, lpToken?.decimals ?? 0)),
        rawValue: balance,
        proportion: holderProportion,
        color: TVL_BREAKDOWN_COLORS[index % TVL_BREAKDOWN_COLORS.length] ?? 'transparent',
        role: 'holder' as const,
        link: `${getExplorerLink(network.name)}/address/${address}`,
      }
    })
    .sort((a, b) => (a.rawValue > b.rawValue ? -1 : a.rawValue < b.rawValue ? 1 : 0))

  const restHoldersCount = Math.max(0, (holdersData?.totalCount ?? 0) - topHolders.length)

  if (restHoldersCount === 0) {
    return { chartData: topHoldersData, restHoldersCount }
  }

  const restHoldersBalance = totalBalance - topHoldersBalance
  const restHoldersProportion = (Number(restHoldersBalance) / Number(balanceDenominator)) * 100

  const restHoldersData: HoldersChartDataCell = {
    label: `Rest ${formatAmount(restHoldersCount)} holders`,
    value: Number(formatUnits(restHoldersBalance, lpToken?.decimals ?? 0)),
    rawValue: restHoldersBalance,
    proportion: restHoldersProportion,
    color: 'rgb(var(--chart-gray))',
    role: 'rest',
  }

  return {
    chartData: [...topHoldersData, restHoldersData],
    restHoldersCount,
  }
}
